import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToastService } from '../../../../shared/services/toast.service';
import { AnimalSheltersRepository } from '../../../../shared/states/animal-shelter.repository';
import { CompetentAuthoritiesRepository } from '../../../../shared/states/competent-authorities.repository';
import { ALLOWED_DOCUMENT_ACCEPT, getDocumentFileMeta } from '../../animal-details-documents/document-file-type.util';
import { AnimalDocumentCategory, MEDICAL_DOCUMENT_CATEGORIES } from '../../models/animal-document-category.enum';
import { AnimalModel } from '../../models/animal.model';
import { AnimalMedicalConditionModel } from '../../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordModel } from '../../models/medical/animal-medical-record.model';
import { AnimalPreventiveTreatmentModel } from '../../models/medical/animal-preventive-treatment.model';
import { AnimalQuarantineModel } from '../../models/medical/animal-quarantine.model';
import {
  getMedicalConditionKindLabelKey,
  getMedicalConditionSeverityLabelKey,
  getMedicalRecordTypeLabelKey,
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  getQuarantineReasonLabelKey,
  MedicalConditionKind,
  MedicalConditionSeverity,
  MedicalRecordType,
  PreventiveTreatmentCategory,
  QuarantineReason
} from '../../models/medical/medical.enums';
import { gramsToKg, parseIsoDate, todayIsoDate, toIsoDateString } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';
import { MedicalRecordSaveService, MedicalSaveResult, MedicalUpload } from '../../services/medical-record-save.service';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';
import {
  activeConditions,
  activeOrPlannedQuarantines,
  isQuarantinePlanned,
  MedicalFile,
  positionsByRecordId,
  treatmentsWithOpenDue
} from '../../states/medical/animal-medical.selectors';
import {
  AttachmentRow,
  buildOperations,
  buildRecordPayload,
  carriedIds,
  ConditionForm,
  ContinueConditionForm,
  ContinueItem,
  ContinueQuarantineForm,
  ContinueTreatmentForm,
  continueConditionChanges,
  continueQuarantineChanges,
  continueTreatmentChanges,
  createConditionForm,
  createContinueConditionForm,
  createContinueQuarantineForm,
  createContinueTreatmentForm,
  createRecordForm,
  createTreatmentForm,
  nextKey,
  RecordForm,
  startOfDay,
  TreatmentForm
} from './medical-record-form';

type DialogMode = 'new' | 'correction' | 'retry';

const MEDIA_ACCEPT = '.jpg,.jpeg,.png,.mp4,.mov';
const DOCUMENT_EXTENSIONS = ALLOWED_DOCUMENT_ACCEPT.split(',');

/**
 * Karteikarten-Modal: einziges Eingabeinstrument des Medizin-Tabs. Speichert Karteikarte,
 * Behandlungen, Diagnosen, Quarantäne, Fortschreibungen und Anhänge zusammen.
 */
const FULLSCREEN_QUERY = window.matchMedia('(max-width: 1024px), (pointer: coarse)');
const DESKTOP_STYLE = { width: '95vw', maxWidth: '95vw', height: '92vh' };
const FULLSCREEN_STYLE = { width: '100vw', maxWidth: '100vw', height: '100dvh', maxHeight: '100dvh', margin: '0', borderRadius: '0' };
const DIALOG_TOKENS = {
  header: { padding: '1.5rem 2rem 1rem' },
  content: { padding: '0.5rem 2rem 1.5rem' },
  footer: { padding: '0 2rem 1.5rem' }
};

@Component({
  selector: 'app-medical-record-dialog',
  templateUrl: './medical-record-dialog.component.html',
  styleUrls: ['../medical-shared.scss'],
  standalone: false
})
export class MedicalRecordDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) animal!: AnimalModel;
  /** Gesetzt: Korrektur-Modus für diesen Eintrag. */
  @Input() correctionOf: AnimalMedicalRecordModel | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() openFile = new EventEmitter<MedicalFile>();

  readonly acceptTypes = `${ALLOWED_DOCUMENT_ACCEPT},${MEDIA_ACCEPT}`;
  protected readonly typeLabelKey = getPreventiveTreatmentTypeLabelKey;
  protected readonly categoryLabelKey = getPreventiveTreatmentCategoryLabelKey;
  protected readonly conditionKindLabelKey = getMedicalConditionKindLabelKey;
  protected readonly severityLabelKey = getMedicalConditionSeverityLabelKey;
  protected readonly reasonLabelKey = getQuarantineReasonLabelKey;
  protected readonly PreventiveTreatmentCategory = PreventiveTreatmentCategory;

  readonly recordTypeOptions = Object.values(MedicalRecordType).map(value => ({ value, label: getMedicalRecordTypeLabelKey(value) }));
  readonly conditionKindOptions = Object.values(MedicalConditionKind).map(value => ({ value, label: getMedicalConditionKindLabelKey(value) }));
  readonly severityOptions = Object.values(MedicalConditionSeverity).map(value => ({ value, label: getMedicalConditionSeverityLabelKey(value) }));
  readonly reasonOptions = Object.values(QuarantineReason).map(value => ({ value, label: getQuarantineReasonLabelKey(value) }));
  readonly documentCategoryOptions = MEDICAL_DOCUMENT_CATEGORIES.map(value => ({ value, label: `animal.documentCategory.${value}` }));
  authorityOptions: { value: number; label: string }[] = [];

  visible = true;
  /** iPad/Tablet: Modal vollflächig statt 95 % wie auf dem Desktop. */
  fullscreen = FULLSCREEN_QUERY.matches;
  protected readonly dialogTokens = DIALOG_TOKENS;
  mode: DialogMode = 'new';
  form: RecordForm = createRecordForm();
  attachments: AttachmentRow[] = [];
  correctionFiles: MedicalFile[] = [];
  dragOver = false;

  continueConditions: ContinueItem<AnimalMedicalConditionModel, ContinueConditionForm>[] = [];
  continueQuarantines: ContinueItem<AnimalQuarantineModel, ContinueQuarantineForm>[] = [];
  continueTreatments: ContinueItem<AnimalPreventiveTreatmentModel, ContinueTreatmentForm>[] = [];

  saving = false;
  submitted = false;
  uploadProgress: { done: number; total: number } | null = null;
  /** Modus „Nachtragen“: die Karteikarte ist gespeichert, nur diese Positionen fehlen noch. */
  savedRecordId: number | null = null;
  pendingKeys = new Set<string>();

  private _carriedTreatmentIds: number[] = [];
  private _carriedConditionIds: number[] = [];
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _saveService: MedicalRecordSaveService,
    private readonly _authoritiesRepository: CompetentAuthoritiesRepository,
    private readonly _sheltersRepository: AnimalSheltersRepository,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService,
    private readonly _confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    fromEvent<MediaQueryListEvent>(FULLSCREEN_QUERY, 'change')
      .pipe(takeUntil(this._destroy$))
      .subscribe(event => this.fullscreen = event.matches);

    const state = this._medicalRepository.getSnapshot();

    if (this.correctionOf) {
      this.mode = 'correction';
      this.prefillFromRecord(this.correctionOf, state.treatments, state.conditions);
      this.correctionFiles = [];
      this._medicalRepository.filesByRecordId$.pipe(take(1)).subscribe(files => {
        this.correctionFiles = files[this.correctionOf!.id] ?? [];
      });
    } else {
      const primary = state.veterinaryContacts.find(contact => contact.isPrimary);
      this.form.controls.veterinarianId.setValue(refId(primary?.contact) ?? null);
      this.buildContinueItems(state.conditions, state.quarantines, state.treatments);
    }

    this.form.controls.quarantineEnabled.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(enabled => {
      this.toggleQuarantine(enabled);
    });
    this.toggleQuarantine(this.form.controls.quarantineEnabled.value);

    this.form.controls.quarantine.controls.unlimited.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(unlimited => {
      const endsAt = this.form.controls.quarantine.controls.endsAt;
      if (unlimited) {
        endsAt.setValue(null);
        endsAt.disable();
      } else {
        endsAt.enable();
      }
    });

    this.form.controls.date.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this.onRecordDateChanged());

    const shelterId = this._sheltersRepository.getActiveAnimalShelter()?.id ?? this.animal.animalShelter?.id;
    if (shelterId) {
      this._authoritiesRepository.getCompetentAuthoritiesByShelterId(shelterId).pipe(take(1)).subscribe(authorities => {
        this.authorityOptions = authorities.map(authority => ({ value: authority.id, label: authority.name }));
      });
    }
  }

  ngOnDestroy(): void {
    this.attachments.forEach(row => row.previewUrl && URL.revokeObjectURL(row.previewUrl));
    this._destroy$.next();
    this._destroy$.complete();
  }

  // --- Zugriffe fürs Template -----------------------------------------------------------------

  get dialogStyle(): Record<string, string> {
    return this.fullscreen ? FULLSCREEN_STYLE : DESKTOP_STYLE;
  }

  get treatments(): TreatmentForm[] {
    return this.form.controls.treatments.controls;
  }

  get conditions(): ConditionForm[] {
    return this.form.controls.conditions.controls;
  }

  get recordDate(): Date | null {
    return this.form.controls.date.value;
  }

  get isRetry(): boolean {
    return this.mode === 'retry';
  }

  get title(): string {
    if (this.correctionOf) {
      return this._translateService.translate('animalMedical.dialog.titleCorrection', { id: this.correctionOf.id });
    }
    return this._translateService.translate('animalMedical.dialog.titleNew');
  }

  get correctionDate(): Date | null {
    return this.correctionOf ? new Date(this.correctionOf.date) : null;
  }

  get quarantineInvalidPeriod(): boolean {
    return !!this.form.controls.quarantine.errors?.['endBeforeStart'];
  }

  /** Im Modus „Nachtragen“ nur noch fehlgeschlagene Positionen zeigen. */
  isPending(key: string): boolean {
    return !this.isRetry || this.pendingKeys.has(key);
  }

  get quarantinePending(): boolean {
    return this.isPending('q');
  }

  get visibleContinueCount(): number {
    return [...this.continueConditions, ...this.continueQuarantines, ...this.continueTreatments]
      .filter(item => this.isPending(item.key)).length;
  }

  get continueChangesCount(): number {
    return this.continueConditions.filter(item => this.isPending(item.key) && continueConditionChanges(item)).length
      + this.continueQuarantines.filter(item => this.isPending(item.key) && continueQuarantineChanges(item)).length
      + this.continueTreatments.filter(item => this.isPending(item.key) && continueTreatmentChanges(item)).length;
  }

  get summary(): { treatments: number; conditions: number; quarantine: number; changes: number; attachments: number; deletions: number } {
    const deletions = this.correctionOf ? this.pendingDeletionCount() : 0;
    return {
      treatments: this.treatments.filter(group => this.isPending(group.controls.key.value)).length,
      conditions: this.conditions.filter(group => this.isPending(group.controls.key.value)).length,
      quarantine: this.form.controls.quarantineEnabled.value && this.quarantinePending ? 1 : 0,
      changes: this.continueChangesCount,
      attachments: this.attachments.filter(row => this.isPending(row.key)).length,
      deletions,
    };
  }

  isContinueConditionChanged(item: ContinueItem<AnimalMedicalConditionModel, ContinueConditionForm>): boolean {
    return !!continueConditionChanges(item);
  }

  isContinueQuarantineChanged(item: ContinueItem<AnimalQuarantineModel, ContinueQuarantineForm>): boolean {
    return !!continueQuarantineChanges(item);
  }

  isContinueTreatmentChanged(item: ContinueItem<AnimalPreventiveTreatmentModel, ContinueTreatmentForm>): boolean {
    return !!continueTreatmentChanges(item);
  }

  isQuarantinePlanned(quarantine: AnimalQuarantineModel): boolean {
    return isQuarantinePlanned(quarantine, todayIsoDate());
  }

  fileIcon(row: AttachmentRow): string {
    if (row.target === 'image') return 'pi pi-image';
    if (row.target === 'video') return 'pi pi-video';
    const meta = getDocumentFileMeta(row.file.name);
    return `${meta.icon} ${meta.colorClass}`;
  }

  // --- Positionen -----------------------------------------------------------------------------

  addTreatment(category: PreventiveTreatmentCategory): void {
    this.form.controls.treatments.push(createTreatmentForm(category));
  }

  removeTreatment(index: number): void {
    this.form.controls.treatments.removeAt(index);
  }

  addCondition(): void {
    this.form.controls.conditions.push(createConditionForm(this.recordDate));
  }

  removeCondition(index: number): void {
    this.form.controls.conditions.removeAt(index);
  }

  markDiagnosedAtManual(group: ConditionForm): void {
    group.controls.diagnosedAtManual.setValue(true);
  }

  markQuarantineStartManual(): void {
    this.form.controls.quarantine.controls.startsAtManual.setValue(true);
  }

  /** „beenden“: Ende auf das Termindatum setzen. */
  endQuarantine(item: ContinueItem<AnimalQuarantineModel, ContinueQuarantineForm>): void {
    item.form.controls.unlimited.setValue(false);
    item.form.controls.endsAt.enable();
    item.form.controls.endsAt.setValue(startOfDay(this.recordDate));
  }

  onContinueQuarantineUnlimited(item: ContinueItem<AnimalQuarantineModel, ContinueQuarantineForm>): void {
    const endsAt = item.form.controls.endsAt;
    if (item.form.controls.unlimited.value) {
      endsAt.setValue(null);
      endsAt.disable();
    } else {
      endsAt.enable();
      endsAt.setValue(parseIsoDate(item.original.endsAt) ?? startOfDay(this.recordDate));
    }
  }

  onContinueTreatmentNoFurtherDue(item: ContinueItem<AnimalPreventiveTreatmentModel, ContinueTreatmentForm>): void {
    const nextDueAt = item.form.controls.nextDueAt;
    if (item.form.controls.noFurtherDue.value) {
      nextDueAt.disable();
    } else {
      nextDueAt.enable();
    }
  }

  // --- Anhänge --------------------------------------------------------------------------------

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  removeAttachment(index: number): void {
    const [row] = this.attachments.splice(index, 1);
    if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
  }

  markAttachmentDateManual(row: AttachmentRow): void {
    row.dateManual = true;
  }

  private addFiles(files: File[]): void {
    const rejected: string[] = [];
    for (const file of files) {
      const target = this.targetFor(file);
      if (!target) {
        rejected.push(file.name);
        continue;
      }
      this.attachments.push({
        key: nextKey('u'),
        file,
        target,
        category: AnimalDocumentCategory.MEDICAL_FINDING,
        date: startOfDay(this.recordDate),
        dateManual: false,
        previewUrl: target === 'image' ? URL.createObjectURL(file) : null,
        upload: null,
      });
    }
    if (rejected.length) {
      this._toast.showError(this._translateService.translate('animalMedical.dialog.attachments.rejected', { files: rejected.join(', ') }));
    }
  }

  private targetFor(file: File): 'document' | 'image' | 'video' | null {
    const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (file.type.startsWith('image/') || ['.jpg', '.jpeg', '.png'].includes(extension)) return 'image';
    if (file.type.startsWith('video/') || ['.mp4', '.mov'].includes(extension)) return 'video';
    return DOCUMENT_EXTENSIONS.includes(extension) ? 'document' : null;
  }

  // --- Speichern ------------------------------------------------------------------------------

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this._toast.showError(this._translateService.translate('animalMedical.dialog.invalid'));
      return;
    }

    const recordDate = this.form.controls.date.value!;
    const operations = buildOperations(this.form, {
      animalId: this.animal.id,
      recordDate,
      veterinarianId: this.form.controls.veterinarianId.value,
      carriedTreatmentIds: this._carriedTreatmentIds,
      carriedConditionIds: this._carriedConditionIds,
      continueConditions: this.continueConditions,
      continueQuarantines: this.continueQuarantines,
      continueTreatments: this.continueTreatments,
    }).filter(operation => this.isPending(operation.key));

    const uploads = this.attachments
      .filter(row => this.isPending(row.key))
      .map(row => this.toUpload(row));

    this.saving = true;
    this.uploadProgress = null;
    this._saveService.save({
      animalId: this.animal.id,
      record: this.savedRecordId === null ? buildRecordPayload(this.form, this.animal.id, this.correctionOf?.id ?? null) : null,
      recordId: this.savedRecordId,
      operations,
      uploads,
    }, (done, total) => this.uploadProgress = { done, total })
      .pipe(take(1))
      .subscribe({
        next: result => this.handleResult(result),
        error: () => {
          this.saving = false;
          this._toast.showError(this._translateService.translate('animalMedical.toast.record.saveError'));
        }
      });
  }

  requestClose(): void {
    if (this.saving) return;
    if (this.isRetry || this.form.dirty || this.attachments.length) {
      this._confirmationService.confirm({
        header: this._translateService.translate('confirmDialog.header.confirmation'),
        message: this._translateService.translate(this.isRetry ? 'animalMedical.dialog.confirmCloseRetry' : 'animalMedical.dialog.confirmClose'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this._translateService.translate('yes'),
        rejectLabel: this._translateService.translate('no'),
        acceptButtonStyleClass: 'p-button-danger p-button-text',
        rejectButtonStyleClass: 'p-button-text',
        accept: () => this.close(),
      });
      return;
    }
    this.close();
  }

  private close(): void {
    this.visible = false;
    this.closed.emit();
  }

  private handleResult(result: MedicalSaveResult): void {
    this.saving = false;
    this.uploadProgress = null;

    if (result.recordId === null) {
      // Nichts gespeichert: Das Modal bleibt voll editierbar.
      this._toast.showError(this._translateService.translate('animalMedical.toast.record.saveError'));
      return;
    }

    const failed = [...result.failedOperations, ...result.failedUploads];
    if (!failed.length) {
      this._toast.showSuccess(this._translateService.translate(
        this.savedRecordId === null ? 'animalMedical.toast.record.saved' : 'animalMedical.toast.record.retrySaved',
        { id: result.recordId }
      ));
      this.close();
      return;
    }

    this.enterRetryMode(result.recordId, failed);
    const labels = this.describeFailures(result.failedOperations, result.failedUploads.length);
    this._toast.showWarn(
      this._translateService.translate('animalMedical.toast.record.partial', {
        id: result.recordId,
        saved: result.savedOperations + result.savedUploads,
        failed: labels,
      }),
      this._translateService.translate('animalMedical.toast.record.partialSummary')
    );
  }

  /** Karteikarte ist gespeichert: Termin/Untersuchung sperren, Id festhalten. */
  private enterRetryMode(recordId: number, failedKeys: string[]): void {
    this.mode = 'retry';
    this.savedRecordId = recordId;
    this.pendingKeys = new Set(failedKeys);
    this.attachments = this.attachments.filter(row => this.pendingKeys.has(row.key));
    this.submitted = false;

    const locked = [
      this.form.controls.date, this.form.controls.type, this.form.controls.veterinarianId,
      this.form.controls.weightKg, this.form.controls.bcsNutrition, this.form.controls.careCondition,
      this.form.controls.finding, this.form.controls.treatment,
    ];
    locked.forEach(control => control.disable({ emitEvent: false }));
    this.form.controls.quarantineEnabled.disable({ emitEvent: false });
    if (!this.pendingKeys.has('q')) {
      this.form.controls.quarantine.disable({ emitEvent: false });
    }
  }

  /** Fehlgeschlagene Positionen nach Art zusammenfassen, z. B. „1 Behandlung, 1 Anhang“. */
  private describeFailures(failedOperationKeys: string[], failedUploads: number): string {
    const counts: Record<string, number> = {};
    for (const key of failedOperationKeys) {
      const bucket = key.startsWith('del-') ? 'deletion'
        : /^c[cqt]-/.test(key) ? 'change'
        : key.startsWith('t-') ? 'treatment'
        : key.startsWith('c-') ? 'condition'
        : 'quarantine';
      counts[bucket] = (counts[bucket] ?? 0) + 1;
    }
    if (failedUploads) counts['upload'] = failedUploads;

    return Object.entries(counts)
      .map(([bucket, count]) => this._translateService.translate(`animalMedical.dialog.failure.${bucket}`, { count }))
      .join(', ');
  }

  /** Das Upload-Objekt bleibt je Zeile erhalten, damit ein bereits hochgeladenes Video nicht doppelt kommt. */
  private toUpload(row: AttachmentRow): MedicalUpload {
    row.upload ??= { key: row.key, file: row.file, target: row.target, category: row.category, date: '', uploadedVideoId: null };
    row.upload.category = row.category;
    row.upload.date = toIsoDateString(row.date ?? this.recordDate ?? new Date())!;
    return row.upload;
  }

  // --- Vorbelegung ----------------------------------------------------------------------------

  private onRecordDateChanged(): void {
    const date = startOfDay(this.recordDate);
    for (const group of this.conditions) {
      if (!group.controls.diagnosedAtManual.value) group.controls.diagnosedAt.setValue(date, { emitEvent: false });
    }
    const quarantine = this.form.controls.quarantine.controls;
    if (!quarantine.startsAtManual.value) quarantine.startsAt.setValue(date, { emitEvent: false });
    for (const row of this.attachments) {
      if (!row.dateManual) row.date = date;
    }
  }

  private toggleQuarantine(enabled: boolean): void {
    const group = this.form.controls.quarantine;
    if (enabled) {
      group.enable({ emitEvent: false });
      if (group.controls.unlimited.value) group.controls.endsAt.disable({ emitEvent: false });
      if (!group.controls.startsAtManual.value) group.controls.startsAt.setValue(startOfDay(this.recordDate), { emitEvent: false });
    } else {
      group.disable({ emitEvent: false });
    }
  }

  private prefillFromRecord(
    record: AnimalMedicalRecordModel,
    treatments: AnimalPreventiveTreatmentModel[],
    conditions: AnimalMedicalConditionModel[]
  ): void {
    this.form.patchValue({
      date: new Date(record.date),
      type: record.type,
      veterinarianId: refId(record.veterinarian),
      weightKg: gramsToKg(record.weight),
      bcsNutrition: record.bcsNutrition,
      careCondition: record.careCondition ?? [],
      finding: record.finding,
      treatment: record.treatment,
    }, { emitEvent: false });

    const positions = positionsByRecordId(treatments, conditions)[record.id] ?? { treatments: [], conditions: [] };
    positions.treatments.forEach(treatment => this.form.controls.treatments.push(createTreatmentForm(treatment.category, treatment)));
    positions.conditions.forEach(condition => this.form.controls.conditions.push(createConditionForm(this.recordDate, condition)));
    this._carriedTreatmentIds = carriedIds(positions.treatments);
    this._carriedConditionIds = carriedIds(positions.conditions);
  }

  private buildContinueItems(
    conditions: AnimalMedicalConditionModel[],
    quarantines: AnimalQuarantineModel[],
    treatments: AnimalPreventiveTreatmentModel[]
  ): void {
    this.continueConditions = activeConditions(conditions).map(condition => ({
      key: `cc-${condition.id}`, original: condition, form: createContinueConditionForm(condition),
    }));
    this.continueQuarantines = activeOrPlannedQuarantines(quarantines, todayIsoDate()).map(quarantine => ({
      key: `cq-${quarantine.id}`, original: quarantine, form: createContinueQuarantineForm(quarantine),
    }));
    this.continueTreatments = treatmentsWithOpenDue(treatments).map(treatment => ({
      key: `ct-${treatment.id}`, original: treatment, form: createContinueTreatmentForm(treatment),
    }));
  }

  private pendingDeletionCount(): number {
    const keptTreatments = new Set(this.treatments.map(group => group.controls.id.value).filter(id => id !== null));
    const keptConditions = new Set(this.conditions.map(group => group.controls.id.value).filter(id => id !== null));
    return this._carriedTreatmentIds.filter(id => !keptTreatments.has(id) && this.isPending(`del-t-${id}`)).length
      + this._carriedConditionIds.filter(id => !keptConditions.has(id) && this.isPending(`del-c-${id}`)).length;
  }
}
